// Pieces the what-if list and result share (spec 03 §4.10, D-472): the status
// chip, a hypothesis in words, the KPI cards, the cost card, the slipped and
// recovered orders, and the promote and discard confirmations.

import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Modal } from "../../../../components/modal";
import { StatStrip, StatTile } from "../../../../components/stat-strip";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import type { Product } from "../hooks/planning-queries";
import type {
  ScenarioCostImpact,
  ScenarioHypothesis,
  ScenarioKpiDiff,
  ScenarioOrderShift,
  ScenarioStatus,
} from "../hooks/scenario-queries";
import { formatExactQty } from "../lib/mrp-lib";
import { formatDate } from "../lib/planning-lib";
import { promotePlan, signedText, whatIfOrderNumber } from "../lib/scenario-lib";
import { formatMoneyMinor } from "../lib/schedule-moves-lib";
import { zonedStamp } from "../lib/schedule-lib";
import { SalesLineText } from "./schedule-bits";

const STATUS_TONE: Record<ScenarioStatus, ChipVariant> = {
  open: "accent",
  solved: "ok",
  failed: "bad",
  promoted: "neutral",
  discarded: "neutral",
};

export function ScenarioStatusChip({ status }: { status: ScenarioStatus }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant={STATUS_TONE[status]}
      dot={status === "open" || status === "solved" || status === "failed"}
      data-testid="planning-scenario-status"
      data-status={status}
    >
      {t(`planning.scenarios.status.${status}`)}
    </StatusChip>
  );
}

/** What a hypothesis names, resolved to words the reader knows. */
export interface HypothesisNames {
  readonly workCenter: (id: string) => string;
  readonly product: (id: string) => Product | undefined;
}

/** One hypothesis as a sentence, its times in the facility's zone. */
export function hypothesisText(
  h: ScenarioHypothesis,
  names: HypothesisNames,
  t: TFunction,
  locale: string,
  timezone: string,
): string {
  const at = (iso: string) => zonedStamp(iso, locale, timezone);
  switch (h.kind) {
    case "add_shift":
      return t("planning.scenarios.result.hypothesis.add_shift", {
        wc: names.workCenter(h.work_center_id),
        start: at(h.starts_at),
        end: at(h.ends_at),
        factor: formatExactQty(h.capacity_factor, locale),
      });
    case "machine_down":
      return t("planning.scenarios.result.hypothesis.machine_down", {
        wc: names.workCenter(h.work_center_id),
        start: at(h.starts_at),
        end: at(h.ends_at),
      });
    case "add_order": {
      const p = names.product(h.product_id);
      return t("planning.scenarios.result.hypothesis.add_order", {
        qty: formatExactQty(h.qty, locale),
        product: p !== undefined ? `${p.sku} · ${p.name}` : "…",
        date: formatDate(h.due_date, locale),
        priority: h.priority,
      });
    }
    case "change_efficiency":
      return t("planning.scenarios.result.hypothesis.change_efficiency", {
        wc: names.workCenter(h.work_center_id),
        shift: h.shift_code,
        pct: h.efficiency_pct,
      });
    case "drop_order":
      return t("planning.scenarios.result.hypothesis.drop_order");
  }
}

/** The hypotheses as a list, each marked saved on promote or not. */
export function HypothesisList({
  hypotheses,
  names,
  timezone,
}: {
  hypotheses: readonly ScenarioHypothesis[];
  names: HypothesisNames;
  timezone: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <ol className="planning-scenario-hypotheses" data-testid="planning-scenario-hypotheses">
      {hypotheses.map((h, i) => (
        <li
          // The list is the scenario's own and never reordered.
          key={i}
          data-testid="planning-scenario-hypothesis"
          data-kind={h.kind}
          data-persistable={String(h.persistable)}
        >
          <span className="planning-scenario-hypothesis-kind">
            {t(`planning.scenarios.kind.${h.kind}`)}
          </span>
          <span>{hypothesisText(h, names, t, i18n.language, timezone)}</span>
          <span
            className={h.persistable ? "planning-hint" : "planning-note"}
            data-testid="planning-scenario-hypothesis-saved"
          >
            {h.persistable
              ? t("planning.scenarios.result.saved")
              : t("planning.scenarios.result.notSaved")}
          </span>
        </li>
      ))}
    </ol>
  );
}

function trendTone(delta: number, lowerIsBetter: boolean): "good" | "bad" | "flat" {
  if (delta === 0) return "flat";
  return delta < 0 === lowerIsBetter ? "good" : "bad";
}

/** The four KPI cards: base → scenario with the signed change. */
export function KpiCards({ diff }: { diff: ScenarioKpiDiff }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const num = (n: number, digits = 0) =>
    new Intl.NumberFormat(lang, { maximumFractionDigits: digits }).format(n);
  const tile = (
    key: "lateOrders" | "lateDays" | "setup" | "makespan",
    metric: { base: number; scenario: number; delta: number },
    digits = 0,
  ) => (
    <StatTile
      key={key}
      data-testid={`planning-scenario-kpi-${key}`}
      label={t(`planning.scenarios.result.kpi.${key}`)}
      value={<span data-value={String(metric.scenario)}>{num(metric.scenario, digits)}</span>}
      trend={{
        text: signedText(metric.delta, lang, digits),
        tone: trendTone(metric.delta, true),
      }}
      sub={
        <span data-testid={`planning-scenario-kpi-${key}-sub`} data-base={String(metric.base)}>
          {t("planning.scenarios.result.kpiSub", {
            base: num(metric.base, digits),
            scenario: num(metric.scenario, digits),
          })}
        </span>
      }
    />
  );
  return (
    <StatStrip data-testid="planning-scenario-kpis">
      {tile("lateOrders", diff.late_orders)}
      {tile("lateDays", diff.total_late_days)}
      {tile("setup", diff.total_setup_minutes)}
      {tile("makespan", diff.makespan_hours, 2)}
    </StatStrip>
  );
}

/** Utilization by work centre: published, scenario and the change, in per cent. */
export function UtilizationTable({
  rows,
  workCenter,
}: {
  rows: ScenarioKpiDiff["utilization_delta"];
  workCenter: (id: string) => string;
}) {
  const { t, i18n } = useTranslation();
  const pct = (n: number) =>
    t("planning.scenarios.result.pct", {
      pct: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(n),
    });
  type Row = ScenarioKpiDiff["utilization_delta"][number];
  const columns: DataTableColumn<Row>[] = [
    {
      key: "wc",
      header: t("planning.scenarios.result.util.workCenter"),
      phonePriority: 1,
      render: (r) => workCenter(r.work_center_id),
    },
    {
      key: "base",
      header: t("planning.scenarios.result.util.base"),
      kind: "qty",
      render: (r) => pct(r.base_pct),
    },
    {
      key: "scenario",
      header: t("planning.scenarios.result.util.scenario"),
      kind: "qty",
      phonePriority: 2,
      render: (r) => pct(r.scenario_pct),
    },
    {
      key: "delta",
      header: t("planning.scenarios.result.util.delta"),
      kind: "qty",
      phonePriority: 3,
      render: (r) => (
        <span data-testid="planning-scenario-util-delta" data-delta={String(r.delta)}>
          {signedText(r.delta, i18n.language, 1)}
        </span>
      ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={[...rows].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))}
      rowKey={(r) => r.work_center_id}
      rowTestId="planning-scenario-util-row"
      rowProps={(r) => ({ "data-work-center-id": r.work_center_id })}
      caption={t("planning.scenarios.result.utilization")}
      fit
      empty={<p className="planning-note">{t("planning.scenarios.result.noUtil")}</p>}
    />
  );
}

/** The cost card, for a reader the server served `cost_impact` to (D-472 ruling 2). */
export function CostCard({ cost }: { cost: ScenarioCostImpact }) {
  const { t, i18n } = useTranslation();
  const priced = cost.overtime_minor !== null && cost.currency !== null;
  return (
    <Card data-testid="planning-scenario-cost">
      <Card.Body>
        <h2 className="planning-subhead">{t("planning.scenarios.result.cost")}</h2>
        <p data-testid="planning-scenario-cost-overtime" data-priced={String(priced)}>
          {t("planning.scenarios.result.overtime")}:{" "}
          {priced
            ? formatMoneyMinor(cost.overtime_minor ?? 0, cost.currency ?? "", i18n.language)
            : t(`planning.scenarios.result.costUnpriced.${cost.unpriced_reason ?? "no_rate"}`)}
        </p>
        <p className="planning-note" data-testid="planning-scenario-cost-penalty">
          {t("planning.scenarios.result.latePenalty")}
        </p>
      </Card.Body>
    </Card>
  );
}

/** The order an order shift names, as the board labels it, or a what-if order. */
function shiftOrderText(s: ScenarioOrderShift, t: TFunction): string {
  const n = whatIfOrderNumber(s.label?.order_no ?? null);
  if (n !== undefined) return t("planning.scenarios.result.whatIfOrder", { n });
  return (
    s.label?.order_no ??
    t(
      s.order_ref.startsWith("work_order:")
        ? "planning.schedule.block.workOrder"
        : "planning.schedule.block.plannedOrder",
    )
  );
}

/** The slipped or recovered orders (≤ 200 listed, beside the exact count). */
export function OrderShiftTable({
  kind,
  rows,
  count,
  timezone,
}: {
  kind: "slipped" | "recovered";
  rows: readonly ScenarioOrderShift[];
  count: number;
  timezone: string;
}) {
  const { t, i18n } = useTranslation();
  const end = (iso: string | null) =>
    iso === null
      ? t("planning.scenarios.result.unplaced")
      : zonedStamp(iso, i18n.language, timezone);
  const columns: DataTableColumn<ScenarioOrderShift>[] = [
    {
      key: "order",
      header: t("planning.scenarios.result.shift.order"),
      phonePriority: 1,
      render: (s) => (
        <span data-testid="planning-scenario-shift-order">{shiftOrderText(s, t)}</span>
      ),
    },
    {
      key: "customer",
      header: t("planning.scenarios.result.shift.customer"),
      phonePriority: 3,
      render: (s) => (s.label !== null ? <SalesLineText label={s.label} /> : ""),
    },
    {
      key: "base",
      header: t("planning.scenarios.result.shift.base"),
      render: (s) => end(s.base_end),
    },
    {
      key: "scenario",
      header: t("planning.scenarios.result.shift.scenario"),
      render: (s) => end(s.scenario_end),
    },
    {
      key: "days",
      header: t("planning.scenarios.result.shift.days"),
      kind: "qty",
      phonePriority: 2,
      render: (s) =>
        s.slip_days === null ? (
          ""
        ) : (
          <span data-testid="planning-scenario-shift-days" data-days={String(s.slip_days)}>
            {signedText(s.slip_days, i18n.language)}
          </span>
        ),
    },
  ];
  return (
    <section className="planning-section" data-testid={`planning-scenario-${kind}`}>
      <h2 className="planning-subhead">{t(`planning.scenarios.result.${kind}`, { count })}</h2>
      <DataTable
        columns={columns}
        rows={[...rows]}
        rowKey={(s) => s.order_ref}
        rowTestId={`planning-scenario-${kind}-row`}
        rowProps={(s) => ({ "data-order-ref": s.order_ref })}
        caption={t(`planning.scenarios.result.${kind}`, { count })}
        fit
        empty={
          <p className="planning-note">
            {t(
              kind === "slipped"
                ? "planning.scenarios.result.slippedNone"
                : "planning.scenarios.result.recoveredNone",
            )}
          </p>
        }
      />
      {count > rows.length ? (
        <p className="planning-hint" data-testid={`planning-scenario-${kind}-more`}>
          {t("planning.scenarios.result.more", { count: count - rows.length })}
        </p>
      ) : null}
    </section>
  );
}

/**
 * The promote confirmation (row 84): exactly what will be written, each
 * extra shift with its work centre and times and each pattern copy with its
 * work centre, and what will not, the non-persistable kinds marked.
 */
export function PromoteDialog({
  hypotheses,
  names,
  timezone,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  hypotheses: readonly ScenarioHypothesis[];
  names: HypothesisNames;
  timezone: string;
  pending: boolean;
  error: string | undefined;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const plan = promotePlan(hypotheses);
  const at = (iso: string) => zonedStamp(iso, i18n.language, timezone);
  const writeLine = (h: ScenarioHypothesis): string => {
    if (h.kind === "add_shift") {
      return t("planning.scenarios.result.confirm.shiftLine", {
        wc: names.workCenter(h.work_center_id),
        start: at(h.starts_at),
        end: at(h.ends_at),
      });
    }
    if (h.kind === "change_efficiency") {
      return t("planning.scenarios.result.confirm.patternLine", {
        wc: names.workCenter(h.work_center_id),
        shift: h.shift_code,
        pct: h.efficiency_pct,
      });
    }
    return hypothesisText(h, names, t, i18n.language, timezone);
  };
  return (
    <Modal
      label={t("planning.scenarios.result.confirm.title")}
      heading={t("planning.scenarios.result.confirm.title")}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={pending}
      className="planning-dialog"
      testId="planning-scenario-promote-dialog"
    >
      <p>{t("planning.scenarios.result.confirm.body")}</p>
      <h3 className="planning-subhead">{t("planning.scenarios.result.confirm.writes")}</h3>
      <ul data-testid="planning-scenario-promote-writes">
        {plan.writes.map(({ index, hypothesis }) => (
          <li key={index} data-kind={hypothesis.kind}>
            {writeLine(hypothesis)}
          </li>
        ))}
      </ul>
      {plan.skips.length > 0 ? (
        <>
          <h3 className="planning-subhead">{t("planning.scenarios.result.confirm.skips")}</h3>
          <ul data-testid="planning-scenario-promote-skips">
            {plan.skips.map(({ index, hypothesis }) => (
              <li key={index} data-kind={hypothesis.kind}>
                {hypothesisText(hypothesis, names, t, i18n.language, timezone)}
                {" · "}
                <span className="planning-note">{t("planning.scenarios.result.notSaved")}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {error !== undefined ? (
        <p className="planning-bad" role="alert" data-testid="planning-scenario-promote-error">
          {error}
        </p>
      ) : null}
      <div className="planning-dialog-actions">
        <Button disabled={pending} onClick={onClose}>
          {t("planning.scenarios.builder.cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={pending}
          data-testid="planning-scenario-promote-confirm"
          onClick={onConfirm}
        >
          {pending
            ? t("planning.scenarios.result.confirm.submitting")
            : t("planning.scenarios.result.confirm.submit")}
        </Button>
      </div>
    </Modal>
  );
}

/** The discard confirmation (row 85). */
export function DiscardDialog({
  pending,
  error,
  onConfirm,
  onClose,
}: {
  pending: boolean;
  error: string | undefined;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      label={t("planning.scenarios.result.discardConfirm.title")}
      heading={t("planning.scenarios.result.discardConfirm.title")}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={pending}
      className="planning-dialog"
      testId="planning-scenario-discard-dialog"
    >
      <p>{t("planning.scenarios.result.discardConfirm.body")}</p>
      {error !== undefined ? (
        <p className="planning-bad" role="alert" data-testid="planning-scenario-discard-error">
          {error}
        </p>
      ) : null}
      <div className="planning-dialog-actions">
        <Button disabled={pending} onClick={onClose}>
          {t("planning.scenarios.builder.cancel")}
        </Button>
        <Button
          variant="danger"
          disabled={pending}
          data-testid="planning-scenario-discard-confirm"
          onClick={onConfirm}
        >
          {t("planning.scenarios.result.discardConfirm.submit")}
        </Button>
      </div>
    </Modal>
  );
}
